import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, ArrowLeft, Plus, Upload, RotateCcw, Download, Trash2, FileSpreadsheet, ChevronDown, Users, CheckCircle, AlertCircle, Loader2, Eye } from 'lucide-react';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { seriesApi } from '../api/series';
import { teamsApi } from '../api/teams';
import { glossaryApi } from '../api/glossary';
import { chaptersApi } from '../api/chapters';
import { jobsApi } from '../api/jobs';
import { costsApi } from '../api/costs';
import { ingestApi } from '../api/ingest';
import type { Job } from '../types';
import type { ParsedFile } from '../utils/parseChapterNumber';
import ChapterDropZone from '../components/ChapterDropZone';
import { exportGlossaryToXlsx } from '../utils/exportGlossaryToXlsx';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import UserBadge from '../components/UserBadge';
import TeamMultiSelect from '../components/TeamMultiSelect';
import { useAuth } from '../contexts/useAuth';
import ExternalSourcesCard from '../components/ExternalSourcesCard';
import TranslationPreviewModal from '../components/TranslationPreviewModal';
import { useToast } from '../contexts/ToastContext';

// Helper function to safely format cost values
const formatCost = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);
  return isNaN(num) ? '0.0000' : num.toFixed(4);
};

// Helper function to safely format numbers with toLocaleString
const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? '0' : num.toLocaleString();
};

function AnimatedCollapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const update = () => {
      setMaxHeight(open ? el.scrollHeight : 0);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  return (
    <div
      className={`overflow-hidden transition-all duration-500 ease-out ${open ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
      style={{ maxHeight }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

export default function SeriesDetailPage() {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
  const [chapterNo, setChapterNo] = useState('');
  const [chapterQuery, setChapterQuery] = useState('');
  const [exportingGlossary, setExportingGlossary] = useState(false);
  const [costDetailsOpen, setCostDetailsOpen] = useState(false);
  const [previewChapter, setPreviewChapter] = useState<{ id: string; no: number } | null>(null);

  const { data: series, isLoading: seriesLoading, error: seriesError } = useQuery({
    queryKey: ['series', seriesId],
    queryFn: () => seriesApi.getById(seriesId!),
    enabled: !!seriesId,
    retry: false,
  });

  // Handle 404 error - redirect to home if series not found
  useEffect(() => {
    if (seriesError) {
      const status = (seriesError as { response?: { status?: number } } | null)?.response?.status;
      if (status === 404) {
        console.log('Series not found, redirecting to home');
        navigate('/');
      }
    }
  }, [seriesError, navigate]);

  const { data: glossary, isLoading: glossaryLoading, error: glossaryError } = useQuery({
    queryKey: ['glossary', seriesId],
    queryFn: () => {
      console.log('Fetching glossary for series:', seriesId);
      return glossaryApi.getBySeries(seriesId!);
    },
    enabled: !!seriesId,
    retry: 2,
  });

  useEffect(() => {
    if (glossary) {
      console.log('Glossary loaded in SeriesDetailPage:', glossary);
    }
  }, [glossary]);

  useEffect(() => {
    if (glossaryError) {
      console.error('Error loading glossary in SeriesDetailPage:', glossaryError);
    }
  }, [glossaryError]);

  const { data: jobs } = useQuery({
    queryKey: ['jobs', seriesId],
    queryFn: () => jobsApi.getAll({ series_id: seriesId! }),
    enabled: !!seriesId,
    // Автоматически обновляем каждые 3 секунды, если есть активные задачи
    refetchInterval: (query) => {
      const data = query.state.data as Job[] | undefined;
      if (data && data.some(j => j.status === 'processing' || j.status === 'queued' || j.status === 'paused')) {
        return 3000; // 3 секунды для активных задач
      }
      return false; // Не обновляем, если все задачи завершены
    },
  });

  const { data: chapters, isLoading: chaptersLoading } = useQuery({
    queryKey: ['chapters', seriesId],
    queryFn: () => chaptersApi.getBySeries(seriesId!),
    enabled: !!seriesId,
    // Обновляем главы каждые 5 секунд, если есть активные задачи
    refetchInterval: () => {
      // Проверяем данные jobs через queryClient
      const jobsData = queryClient.getQueryData<Job[]>(['jobs', seriesId]);
      if (jobsData && jobsData.some(j => j.status === 'processing' || j.status === 'queued' || j.status === 'paused')) {
        return 5000; // 5 секунд, если есть активные задачи
      }
      return false; // Не обновляем, если все задачи завершены
    },
  });

  const { data: costs } = useQuery({
    queryKey: ['costs', seriesId],
    queryFn: () => costsApi.getBySeries(seriesId!),
    enabled: !!seriesId,
    refetchInterval: 30000, // Обновляем каждые 30 секунд
    retry: false, // Don't retry on 404 - table might not exist yet
  });

  // Inline upload state
  const [uploadFiles, setUploadFiles] = useState<ParsedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<Array<{
    filename: string;
    chapterNo: number | null;
    status: 'pending' | 'uploading' | 'success' | 'error';
    jobId?: string;
    error?: string;
  }>>([]);
  const [showDropZone, setShowDropZone] = useState(false);

  const [downloading, setDownloading] = useState<{ jobId: string; format: 'txt' | 'json' | 'docx' } | null>(null);

  const getStepLabel = (step: string | undefined): string => {
    if (!step) return '';
    const labels: Record<string, string> = {
      waiting: 'Ожидание',
      downloading: 'Скачивание',
      downloaded: 'Скачано',
      download_failed: 'Ошибка скачивания',
      normalizing_scans: 'Нормализация',
      smartstitch: 'SmartStitch',
      ocr: 'Распознавание текста',
      translating: 'Перевод',
      queued: 'В очереди',
    };
    return labels[step] ?? step;
  };

  const getJobStatusLabel = (job: Job) => {
    const step = job.progress?.step as string | undefined;
    if (job.status === 'queued') {
      if (step === 'waiting' || step === 'downloading' || step === 'downloaded') {
        return getStepLabel(step);
      }
      return 'В очереди';
    }
    if (job.status === 'processing') {
      if (step) return getStepLabel(step);
      return 'Обрабатывается';
    }
    if (job.status === 'paused') return 'Пауза';
    if (job.status === 'completed') return 'Завершена';
    if (job.status === 'failed') {
      if (step === 'download_failed') return 'Ошибка скачивания';
      return 'Ошибка';
    }
    return job.status;
  };

  const getJobStatusBadgeClass = (job: Job) => {
    const step = job.progress?.step as string | undefined;
    if (job.status === 'completed') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
    if (job.status === 'processing') {
      if (step === 'ocr' || step === 'normalizing_scans' || step === 'smartstitch') {
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200';
      }
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
    }
    if (job.status === 'queued') {
      if (step === 'downloading') return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200';
      if (step === 'downloaded') return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200';
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
    }
    if (job.status === 'paused') return 'bg-slate-200 text-slate-800 dark:bg-slate-900/40 dark:text-slate-200';
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200';
  };

  const getChapterStatusBadgeClass = (status: string) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
    if (status === 'processing') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
    if (status === 'failed') return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200';
    return 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200';
  };

  const createChapterMutation = useMutation({
    mutationFn: (data: { series_id: string; chapter_no: number }) =>
      chaptersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
      setIsChapterModalOpen(false);
      setChapterNo('');
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => jobsApi.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', seriesId] });
      toast('Задача перезапущена', 'success');
    },
    onError: () => toast('Не удалось перезапустить задачу', 'error'),
  });

  const deleteSeriesMutation = useMutation({
    mutationFn: (id: string) => seriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series'] });
      toast('Серия удалена', 'info');
      navigate('/');
    },
    onError: () => toast('Не удалось удалить серию', 'error'),
  });

  const deleteChapterMutation = useMutation({
    mutationFn: (id: string) => chaptersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
      toast('Глава удалена', 'info');
    },
    onError: () => toast('Не удалось удалить главу', 'error'),
  });

  const toggleCleanExportMutation = useMutation({
    mutationFn: (cleanExport: boolean) => seriesApi.update(seriesId!, { clean_export: cleanExport }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series', seriesId] });
    },
  });

  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getAll(),
  });

  const changeTeamsMutation = useMutation({
    mutationFn: (teamIds: string[]) => seriesApi.update(seriesId!, { team_ids: teamIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series', seriesId] });
      queryClient.invalidateQueries({ queryKey: ['series'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const handleDeleteSeries = () => {
    if (!series || !seriesId) return;
    if (window.confirm(`Вы уверены, что хотите удалить серию "${series.title}"? Это действие нельзя отменить, и будут удалены все связанные данные (главы, глоссарий и т.д.).`)) {
      deleteSeriesMutation.mutate(seriesId);
    }
  };

  const handleDeleteChapter = (chapterId: string, chapterNo: number) => {
    if (window.confirm(`Вы уверены, что хотите удалить главу ${chapterNo}? Это действие нельзя отменить, и будут удалены все связанные данные (страницы, переводы и т.д.).`)) {
      deleteChapterMutation.mutate(chapterId);
    }
  };

  const download = async (jobId: string, format: 'txt' | 'json' | 'docx') => {
    setDownloading({ jobId, format });
    try {
      const { blob, filename } = await jobsApi.downloadResult(jobId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  const handleExportGlossary = () => {
    if (!glossary || glossary.length === 0) {
      toast('Глоссарий пуст — нечего скачивать', 'info');
      return;
    }
    setExportingGlossary(true);
    try {
      exportGlossaryToXlsx({ terms: glossary, seriesTitle: series?.title });
    } finally {
      setExportingGlossary(false);
    }
  };

  const hasOutputFiles = (j: Job) => {
    const outputFiles = j.progress?.['outputFiles'];
    return !!outputFiles && typeof outputFiles === 'object';
  };

  const handleRetry = (jobId: string) => {
    if (window.confirm('Перезапустить задачу?')) {
      retryMutation.mutate(jobId);
    }
  };

  const handleCreateChapter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!seriesId || !chapterNo.trim()) return;
    createChapterMutation.mutate({
      series_id: seriesId,
      chapter_no: parseInt(chapterNo, 10),
    });
  };

  // Inline upload handlers
  const existingChapterNos = new Set(chapters?.map(c => c.chapter_no) ?? []);

  const handleUploadFilesSelected = (parsedFiles: ParsedFile[]) => {
    setUploadFiles(parsedFiles);
    setUploadResults([]);
  };

  const uploadAllValid = uploadFiles.length > 0 && uploadFiles.every(f => f.isValid);

  const handleInlineUpload = async () => {
    if (!uploadFiles.length || !seriesId || !uploadAllValid) return;

    setIsUploading(true);
    const results = uploadFiles.map(f => ({
      filename: f.filename,
      chapterNo: f.chapterNo,
      status: 'pending' as const,
    }));
    setUploadResults(results);

    const concurrency = 3;
    let currentIndex = 0;

    const uploadNext = async (): Promise<void> => {
      const index = currentIndex++;
      if (index >= uploadFiles.length) return;

      const file = uploadFiles[index];

      setUploadResults(prev => {
        const next = [...prev];
        next[index] = { ...next[index], status: 'uploading' };
        return next;
      });

      try {
        const response = await ingestApi.upload(
          file.file,
          seriesId,
          file.chapterNo ?? undefined,
          undefined
        );

        setUploadResults(prev => {
          const next = [...prev];
          next[index] = { ...next[index], status: 'success', jobId: response.job_id };
          return next;
        });
      } catch (error: unknown) {
        const axiosError = error as { response?: { status?: number; data?: { error?: string; message?: string } } };
        const errorMessage =
          axiosError?.response?.status === 413
            ? 'Файл слишком большой'
            : axiosError?.response?.data?.error ||
              axiosError?.response?.data?.message ||
              'Ошибка загрузки';

        setUploadResults(prev => {
          const next = [...prev];
          next[index] = { ...next[index], status: 'error', error: errorMessage };
          return next;
        });
      }

      await uploadNext();
    };

    const promises = [];
    for (let i = 0; i < Math.min(concurrency, uploadFiles.length); i++) {
      promises.push(uploadNext());
    }
    await Promise.all(promises);

    setIsUploading(false);
    queryClient.invalidateQueries({ queryKey: ['jobs', seriesId] });
    queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
  };

  const handleClearUpload = () => {
    setUploadFiles([]);
    setUploadResults([]);
    setShowDropZone(false);
  };

  const uploadSuccessCount = uploadResults.filter(r => r.status === 'success').length;
  const uploadErrorCount = uploadResults.filter(r => r.status === 'error').length;
  const uploadAllDone = uploadResults.length > 0 && uploadResults.every(r => r.status === 'success' || r.status === 'error');

  // Keyboard shortcuts: Ctrl+Enter to start upload
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (uploadFiles.length > 0 && uploadAllValid && !isUploading) {
          e.preventDefault();
          handleInlineUpload();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [uploadFiles, uploadAllValid, isUploading]);

  const getJobForChapter = (chapterId: string) => {
    return jobs?.find((job) => job.chapter_id === chapterId);
  };

  if (seriesLoading) return <LoadingSpinner />;
  if (!series) return <ErrorMessage message="Серия не найдена" />;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/">
          <Button variant="secondary" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад к списку
          </Button>
        </Link>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/60 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {series.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-gray-500 dark:text-gray-400">
              <span>Исходный язык: {series.source_language.toUpperCase()}</span>
              {teams && teams.length > 0 ? (
                <TeamMultiSelect
                  teams={teams}
                  selectedIds={(series.teams ?? []).map(t => t.id)}
                  onChange={(ids) => changeTeamsMutation.mutate(ids)}
                  disabled={changeTeamsMutation.isPending}
                  inline
                />
              ) : (
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  {series.teams && series.teams.length > 0 ? (
                    series.teams.map((t) => (
                      <span
                        key={t.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {t.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm">Без команды</span>
                  )}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <UserBadge username={series.created_by_username} size="md" />
              {series.created_at && (
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  {formatRelativeTime(series.created_at)}
                </span>
              )}
            </div>
          </div>
          {(isAdmin || series.created_by_user_id === user?.id) && (
          <Button
            variant="secondary"
            onClick={handleDeleteSeries}
            disabled={deleteSeriesMutation.isPending}
            className="w-full sm:w-auto flex items-center justify-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Удалить серию
          </Button>
          )}
        </div>

        {/* Clean export toggle */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!series.clean_export}
                disabled={toggleCleanExportMutation.isPending}
                onChange={(e) => toggleCleanExportMutation.mutate(e.target.checked)}
              />
              <div className="w-10 h-5 bg-gray-300 dark:bg-gray-600 rounded-full peer-checked:bg-primary-500 peer-disabled:opacity-50 transition-colors" />
              <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                Чистый экспорт
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                В файлах TXT и Word — только перевод, без оригинала и служебных строк
              </p>
            </div>
          </label>
        </div>
      </div>

      {costs && (
        <Card title="Статистика затрат" className="hover:shadow-md transition-shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-900/40 hover:shadow-sm transition-all hover:-translate-y-0.5">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Общая стоимость</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${formatCost(costs.total_cost_usd)}
              </div>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-900/40 hover:shadow-sm transition-all hover:-translate-y-0.5">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Всего токенов</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatNumber(costs.total_tokens)}
              </div>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-900/40 hover:shadow-sm transition-all hover:-translate-y-0.5">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Операций</div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {costs.operations_count || 0}
              </div>
            </div>
          </div>

          {(costs.models_used && costs.models_used.length > 0) ||
          (costs.breakdown_by_operation && costs.breakdown_by_operation.length > 0) ||
          (costs.breakdown_by_chapter && costs.breakdown_by_chapter.length > 0) ? (
            <div className="mt-4 flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCostDetailsOpen((v) => !v)}
                aria-expanded={costDetailsOpen}
                className="flex items-center"
              >
                {costDetailsOpen ? 'Скрыть детализацию' : 'Показать детализацию'}
                <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${costDetailsOpen ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          ) : null}

          <AnimatedCollapse open={costDetailsOpen}>
            <div className="pt-4 space-y-4">
              {costs.models_used && costs.models_used.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Использованные модели:</div>
                  <div className="space-y-2">
                    {costs.models_used.map((model, idx) => (
                      <div key={idx} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">{model.model}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({model.provider})
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 sm:text-right">
                          ${formatCost(model.cost)} ({model.operations} операций)
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {costs.breakdown_by_operation && costs.breakdown_by_operation.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">По типам операций:</div>
                  <div className="space-y-2">
                    {costs.breakdown_by_operation.map((op, idx) => (
                      <div key={idx} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {op.operation_type === 'translation' ? 'Перевод' :
                             op.operation_type === 'ocr' ? 'OCR' :
                             op.operation_type === 'glossary_extraction' ? 'Извлечение глоссария' :
                             op.operation_type === 'embedding' ? 'Эмбеддинги' :
                             op.operation_type}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({op.count} операций)
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 sm:text-right">
                          ${formatCost(op.total_cost_usd)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {costs.breakdown_by_chapter && costs.breakdown_by_chapter.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">По главам:</div>
                  <div className="space-y-2">
                    {costs.breakdown_by_chapter.map((chapter, idx) => {
                      const chapterData = chapters?.find(c => c.id === chapter.chapter_id);
                      return (
                        <div key={idx} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              Глава {chapter.chapter_no || chapterData?.chapter_no || '?'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              ({chapter.operations_count ?? 0} операций, {formatNumber(chapter.total_tokens)} токенов)
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white sm:text-right">
                            ${formatCost(chapter.total_cost_usd)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </AnimatedCollapse>
        </Card>
      )}

      {/* External Sources */}
      <ExternalSourcesCard seriesId={seriesId!} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Глоссарий" className="hover:shadow-md transition-shadow">
          {glossaryLoading ? (
            <LoadingSpinner />
          ) : glossaryError ? (
            <div className="text-sm text-red-600 dark:text-red-400">
              Ошибка загрузки глоссария
            </div>
          ) : (
            <div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Терминов: {glossary?.length || 0}
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleExportGlossary}
                    disabled={glossaryLoading || exportingGlossary || !glossary || glossary.length === 0}
                    title="Скачать глоссарий в формате Excel (.xlsx)"
                    className="w-full sm:w-auto flex items-center justify-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Скачать XLSX
                  </Button>
                  <Link to={`/series/${seriesId}/glossary`}>
                    <Button size="sm" className="w-full sm:w-auto flex items-center justify-center">Управление</Button>
                  </Link>
                </div>
              </div>
              {glossary && glossary.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {glossary.slice(0, 10).map((term) => (
                    <div
                      key={term.id}
                      className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {term.source_term}
                            </span>
                            {term.attribute && (
                              <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                                {term.attribute}
                              </span>
                            )}
                          </div>
                          <span className="text-primary-600 dark:text-primary-400 block mt-1">
                            {term.ru_term}
                          </span>
                        </div>
                      </div>
                      {term.notes && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          {term.notes}
                        </p>
                      )}
                    </div>
                  ))}
                  {glossary.length > 10 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center pt-2">
                      И ещё {glossary.length - 10} терминов...
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Глоссарий пуст
                </p>
              )}
            </div>
          )}
        </Card>

        <Card title="Главы" className="hover:shadow-md transition-shadow">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Глав: {chapters?.length || 0}
              </p>
              <div className="relative w-full sm:w-64">
                <Input
                  value={chapterQuery}
                  onChange={(e) => setChapterQuery(e.target.value)}
                  placeholder="Номер, статус, задача…"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="w-full sm:w-auto flex items-center justify-center" onClick={() => setShowDropZone(!showDropZone)}>
                <Upload className="h-4 w-4 mr-1" />
                {showDropZone ? 'Скрыть загрузку' : 'Загрузить главы'}
              </Button>
              <Button size="sm" className="w-full sm:w-auto flex items-center justify-center" onClick={() => setIsChapterModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Добавить главу
              </Button>
            </div>
          </div>

          {/* Inline upload zone */}
          {showDropZone && (
            <div className="mb-4 p-4 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50">
              <ChapterDropZone
                onFilesSelected={handleUploadFilesSelected}
                disabled={isUploading}
              />

              {/* Conflict warnings for existing chapters */}
              {uploadFiles.length > 0 && uploadFiles.some(f => f.chapterNo !== null && existingChapterNos.has(f.chapterNo)) && (
                <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Обнаружены существующие главы:
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                      {uploadFiles
                        .filter(f => f.chapterNo !== null && existingChapterNos.has(f.chapterNo!))
                        .map(f => `Глава ${f.chapterNo}`)
                        .join(', ')}
                      {' '}— архив будет загружен повторно (дедупликация на сервере).
                    </p>
                  </div>
                </div>
              )}

              {/* Upload button */}
              {uploadFiles.length > 0 && !uploadAllDone && (
                <div className="mt-3">
                  <Button
                    onClick={handleInlineUpload}
                    disabled={!uploadAllValid || isUploading}
                    className="w-full"
                    size="sm"
                  >
                    {isUploading ? (
                      <span className="flex items-center justify-center">
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Загрузка... ({uploadResults.filter(r => r.status === 'success' || r.status === 'error').length}/{uploadFiles.length})
                      </span>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Загрузить {uploadFiles.length} {uploadFiles.length === 1 ? 'главу' : uploadFiles.length < 5 ? 'главы' : 'глав'}
                      </>
                    )}
                  </Button>
                  {!isUploading && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 text-center">Ctrl+Enter для быстрой загрузки</p>
                  )}
                </div>
              )}

              {/* Upload results */}
              {uploadResults.length > 0 && (
                <div className="mt-3 space-y-2">
                  {uploadAllDone && (
                    <div className={`p-3 rounded-lg flex items-center justify-between ${
                      uploadErrorCount === 0
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                    }`}>
                      <div className="flex items-center">
                        {uploadErrorCount === 0 ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                            <span className="text-sm text-green-800 dark:text-green-300">
                              Все {uploadSuccessCount} глав загружены!
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2" />
                            <span className="text-sm text-amber-800 dark:text-amber-300">
                              Загружено {uploadSuccessCount}, ошибок: {uploadErrorCount}
                            </span>
                          </>
                        )}
                      </div>
                      <Button variant="secondary" size="sm" onClick={handleClearUpload}>
                        Закрыть
                      </Button>
                    </div>
                  )}

                  {uploadResults.map((result, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                        result.status === 'success'
                          ? 'bg-green-50 dark:bg-green-900/10'
                          : result.status === 'error'
                          ? 'bg-red-50 dark:bg-red-900/10'
                          : result.status === 'uploading'
                          ? 'bg-blue-50 dark:bg-blue-900/10'
                          : 'bg-gray-50 dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {result.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />}
                        {result.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                        {result.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                        {result.status === 'pending' && <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                        <span className="font-medium truncate">Гл. {result.chapterNo ?? '?'}</span>
                        <span className="text-gray-400 truncate">{result.filename}</span>
                      </div>
                      <div className="text-right ml-2 flex-shrink-0">
                        {result.status === 'success' && result.jobId && (
                          <span className="text-green-600 dark:text-green-400 font-mono text-xs">
                            {result.jobId.slice(0, 8)}…
                          </span>
                        )}
                        {result.status === 'error' && (
                          <span className="text-red-600 dark:text-red-400 text-xs break-words max-w-xs inline-block">{result.error}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {chaptersLoading ? (
            <LoadingSpinner />
          ) : chapters && chapters.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {[...chapters]
                .sort((a, b) => (a.chapter_no ?? 0) - (b.chapter_no ?? 0))
                .filter((chapter) => {
                  const q = chapterQuery.trim().toLowerCase();
                  if (!q) return true;
                  const job = getJobForChapter(chapter.id);
                  const haystack = [
                    String(chapter.chapter_no ?? ''),
                    chapter.status ?? '',
                    job?.status ?? '',
                    job?.id ?? '',
                  ]
                    .join(' ')
                    .toLowerCase();
                  return haystack.includes(q);
                })
                .map((chapter) => {
                const job = getJobForChapter(chapter.id);
                return (
                  <details
                    key={chapter.id}
                    className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-700/40 hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    <summary className="list-none cursor-pointer px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-lg bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-gray-900 dark:text-white truncate">
                              Глава {chapter.chapter_no}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getChapterStatusBadgeClass(chapter.status)}`}>
                              {chapter.status}
                            </span>
                            {job && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getJobStatusBadgeClass(job)}`}>
                                {getJobStatusLabel(job)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-300 mt-1">
                            {job && (
                              <>
                                <span>
                                  Задача <span className="font-mono">{job.id.slice(0, 8)}…</span>
                                </span>
                                {job.progress?.step && (job.status === 'processing' || job.status === 'queued') && (
                                  <span className="text-primary-600 dark:text-primary-400">
                                    {getStepLabel(job.progress.step as string)}
                                    {job.progress.current_page != null && job.progress.total_pages != null
                                      ? ` (${job.progress.current_page}/${job.progress.total_pages})`
                                      : job.progress.progress != null && Number(job.progress.progress) > 0
                                      ? ` ${job.progress.progress}%`
                                      : ''}
                                  </span>
                                )}
                              </>
                            )}
                            <UserBadge username={chapter.created_by_username} />
                          </div>
                          {chapter.translation_coverage != null && chapter.translation_coverage > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    chapter.translation_coverage >= 100
                                      ? 'bg-emerald-500'
                                      : chapter.translation_coverage >= 50
                                        ? 'bg-blue-500'
                                        : 'bg-amber-500'
                                  }`}
                                  style={{ width: `${Math.min(chapter.translation_coverage, 100)}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                {chapter.translation_coverage}% переведено
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-gray-400 dark:text-gray-300 transition-transform group-[open]:rotate-180">
                        <ChevronDown className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-4 pb-4">
                      <div className="pt-1">
                        <div className="flex flex-wrap gap-2 justify-end">
                          {job && (
                            <>
                              {job.status === 'failed' && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleRetry(job.id)}
                                  disabled={retryMutation.isPending}
                                  title="Перезапустить задачу"
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Retry
                                </Button>
                              )}
                              {job.status === 'completed' && hasOutputFiles(job) && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => download(job.id, 'txt')}
                                    disabled={!!downloading}
                                    title="Скачать TXT"
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    TXT
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => download(job.id, 'json')}
                                    disabled={!!downloading}
                                    title="Скачать JSON"
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    JSON
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => download(job.id, 'docx')}
                                    disabled={!!downloading}
                                    title="Скачать DOCX"
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    DOCX
                                  </Button>
                                </>
                              )}
                              {job.status === 'completed' && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setPreviewChapter({ id: chapter.id, no: chapter.chapter_no })}
                                  title="Предпросмотр перевода"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Перевод
                                </Button>
                              )}
                              <Link to={`/jobs/${job.id}`}>
                                <Button size="sm" variant="secondary">
                                  Детали
                                </Button>
                              </Link>
                            </>
                          )}
                          {!job && (
                            <Link to={`/upload?series_id=${encodeURIComponent(seriesId!)}&chapter_no=${encodeURIComponent(String(chapter.chapter_no))}`}>
                              <Button size="sm" variant="secondary">
                                <Upload className="h-4 w-4 mr-1" />
                                Загрузить архив
                              </Button>
                            </Link>
                          )}
                          {(isAdmin || chapter.created_by_user_id === user?.id || series.created_by_user_id === user?.id) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleDeleteChapter(chapter.id, chapter.chapter_no)}
                            disabled={deleteChapterMutation.isPending}
                            title="Удалить главу"
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Удалить
                          </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Главы не добавлены
            </p>
          )}
        </Card>
      </div>

      <Modal
        isOpen={isChapterModalOpen}
        onClose={() => setIsChapterModalOpen(false)}
        title="Добавить главу"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsChapterModalOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleCreateChapter}
              disabled={!chapterNo.trim() || createChapterMutation.isPending}
            >
              Добавить
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateChapter} className="space-y-4">
          <Input
            label="Номер главы"
            type="number"
            value={chapterNo}
            onChange={(e) => setChapterNo(e.target.value)}
            required
            placeholder="Введите номер главы"
            min="1"
          />
        </form>
      </Modal>

      {previewChapter && (
        <TranslationPreviewModal
          isOpen={!!previewChapter}
          onClose={() => setPreviewChapter(null)}
          chapterId={previewChapter.id}
          chapterNo={previewChapter.no}
        />
      )}
    </div>
  );
}
